import React, { useEffect, useRef } from 'react'
import { useTheme } from '../../../contexts/ThemeContext'
import './ParticleBackground.css'

const ParticleBackground = () => {
  const canvasRef = useRef(null)
  const { isDarkMode } = useTheme()
  const mouseRef = useRef({ x: 0, y: 0 })

  useEffect(() => {
    const canvas = canvasRef.current
    if (!canvas) return

    const ctx = canvas.getContext('2d')
    let animationId

    // Set canvas size
    const resizeCanvas = () => {
      canvas.width = canvas.offsetWidth
      canvas.height = canvas.offsetHeight
    }

    resizeCanvas()
    window.addEventListener('resize', resizeCanvas)

    // Mouse tracking
    const handleMouseMove = (event) => {
      const rect = canvas.getBoundingClientRect()
      mouseRef.current = {
        x: event.clientX - rect.left,
        y: event.clientY - rect.top
      }
      // Temporary debug - remove later
      console.log('Mouse position:', mouseRef.current.x, mouseRef.current.y)
    }

    const handleMouseLeave = () => {
      mouseRef.current = { x: -1000, y: -1000 } // Move mouse far away
      console.log('Mouse left canvas')
    }

    canvas.addEventListener('mousemove', handleMouseMove)
    canvas.addEventListener('mouseleave', handleMouseLeave)

    // Particle system
    const particles = []
    const particleCount = 60

    // Particle class
    class Particle {
      constructor() {
        this.x = Math.random() * canvas.width
        this.y = Math.random() * canvas.height
        this.vx = (Math.random() - 0.5) * 2
        this.vy = (Math.random() - 0.5) * 2
        this.originalVx = this.vx
        this.originalVy = this.vy
        this.size = Math.random() * 3 + 1
        this.originalSize = this.size
        this.opacity = Math.random() * 0.5 + 0.2
        this.originalOpacity = this.opacity
      }

      update() {
        // Mouse interaction
        const mouse = mouseRef.current
        const dx = this.x - mouse.x
        const dy = this.y - mouse.y
        const distance = Math.sqrt(dx * dx + dy * dy)
        const maxDistance = 120

        if (distance < maxDistance) {
          // Repel from mouse
          const force = (maxDistance - distance) / maxDistance
          const angle = Math.atan2(dy, dx)
          this.vx = this.originalVx + Math.cos(angle) * force * 3
          this.vy = this.originalVy + Math.sin(angle) * force * 3
          
          // Increase size and opacity when near mouse
          this.size = this.originalSize * (1 + force * 0.5)
          this.opacity = Math.min(1, this.originalOpacity * (1 + force))
        } else {
          // Return to original velocity gradually
          this.vx += (this.originalVx - this.vx) * 0.05
          this.vy += (this.originalVy - this.vy) * 0.05
          this.size += (this.originalSize - this.size) * 0.05
          this.opacity += (this.originalOpacity - this.opacity) * 0.05
        }

        this.x += this.vx
        this.y += this.vy

        // Bounce off walls
        if (this.x < 0 || this.x > canvas.width) {
          this.vx *= -1
          this.originalVx *= -1
        }
        if (this.y < 0 || this.y > canvas.height) {
          this.vy *= -1
          this.originalVy *= -1
        }
      }

      draw() {
        ctx.save()
        ctx.globalAlpha = this.opacity
        
        // Create gradient for particle
        const gradient = ctx.createRadialGradient(
          this.x, this.y, 0,
          this.x, this.y, this.size
        )
        
        if (isDarkMode) {
          gradient.addColorStop(0, '#a855f7')
          gradient.addColorStop(1, 'rgba(168, 85, 247, 0)')
        } else {
          gradient.addColorStop(0, '#073954')
          gradient.addColorStop(1, 'rgba(7, 57, 84, 0)')
        }
        
        ctx.fillStyle = gradient
        ctx.beginPath()
        ctx.arc(this.x, this.y, this.size, 0, Math.PI * 2)
        ctx.fill()
        ctx.restore()
      }
    }

    // Initialize particles
    for (let i = 0; i < particleCount; i++) {
      particles.push(new Particle())
    }

    // Animation loop
    const animate = () => {
      ctx.clearRect(0, 0, canvas.width, canvas.height)

      particles.forEach(particle => {
        particle.update()
        particle.draw()
      })

      // Draw connections between particles
      ctx.strokeStyle = isDarkMode ? 'rgba(192, 132, 252, 0.2)' : 'rgba(25, 118, 210, 0.2)'
      ctx.lineWidth = 1

      for (let i = 0; i < particles.length; i++) {
        for (let j = i + 1; j < particles.length; j++) {
          const dx = particles[i].x - particles[j].x
          const dy = particles[i].y - particles[j].y
          const distance = Math.sqrt(dx * dx + dy * dy)

          if (distance < 100) {
            ctx.globalAlpha = (100 - distance) / 100 * 0.3
            ctx.beginPath()
            ctx.moveTo(particles[i].x, particles[i].y)
            ctx.lineTo(particles[j].x, particles[j].y)
            ctx.stroke()
          }
        }
      }

      // Draw connections from mouse to nearby particles
      const mouse = mouseRef.current
      if (mouse.x > 0 && mouse.y > 0 && mouse.x < canvas.width && mouse.y < canvas.height) {
        ctx.strokeStyle = isDarkMode ? 'rgba(168, 85, 247, 0.4)' : 'rgba(7, 57, 84, 0.4)'
        ctx.lineWidth = 2

        particles.forEach(particle => {
          const dx = particle.x - mouse.x
          const dy = particle.y - mouse.y
          const distance = Math.sqrt(dx * dx + dy * dy)

          if (distance < 150) {
            ctx.globalAlpha = (150 - distance) / 150 * 0.5
            ctx.beginPath()
            ctx.moveTo(particle.x, particle.y)
            ctx.lineTo(mouse.x, mouse.y)
            ctx.stroke()
          }
        })
      }

      ctx.globalAlpha = 1
      animationId = requestAnimationFrame(animate)
    }

    animate()

    return () => {
      if (animationId) {
        cancelAnimationFrame(animationId)
      }
      window.removeEventListener('resize', resizeCanvas)
      canvas.removeEventListener('mousemove', handleMouseMove)
      canvas.removeEventListener('mouseleave', handleMouseLeave)
    }
  }, [isDarkMode])

  return (
    <canvas
      ref={canvasRef}
      className="particle-background"
    />
  )
}

export default ParticleBackground
